/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react'
import { getEZUIKitPlayerClass } from './ensureEZUIKit'
import { buildLiveEzopenUrl } from './buildEzopenUrl'

const defaultLiveOptions = {
  autoplay: true,
  template: 'simple',
  header: false,
  footer: false,
  openSound: false,
  streamInfoCBType: 1
}

function resolvePlayUrl ({ url, deviceSerial, channelNo, mode }) {
  if (url) return url
  if (mode === 'playback') return ''
  return buildLiveEzopenUrl(deviceSerial, channelNo)
}

const overlayBaseStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  cursor: 'pointer',
  zIndex: 10,
  padding: 8,
  background: 'rgba(0,0,0,0.45)'
}

/**
 * SDK 宽高用挂载点 layout 尺寸（offsetWidth），不用 getBoundingClientRect。
 * 可视化大屏等祖先有 transform:scale 时，rect 是缩放后的视觉值；拿去建播放器会按小分辨率出画，再被 scale 拉伸 → 模糊、裁切。
 * 不读 offsetParent / 父级（曾干扰 EZUIKit 首帧）。
 */
function measureMountRectForSdk (el) {
  if (!el) return { w: 320, h: 180 }
  let w = el.offsetWidth || el.clientWidth || 0
  let h = el.offsetHeight || el.clientHeight || 0
  if (w < 8 || h < 8) {
    const r = el.getBoundingClientRect()
    w = r.width || 0
    h = r.height || 0
  }
  if (w < 8) w = 320
  if (h < 8) h = 180
  return { w: Math.floor(w), h: Math.floor(h) }
}

/**
 * handleSuccess 后再对齐一次尺寸（PlaybackForm 回放页同款思路）。
 * 只调用 resize 一次，禁止与 reSize 双调防止 SDK 内部状态错乱。
 */
function scheduleResizeAfterReady (player, domId) {
  const run = () => {
    try {
      if (!player || typeof player.resize !== 'function') return
      const el = typeof domId === 'string' ? document.getElementById(domId) : domId
      if (!el) return
      const { w, h } = measureMountRectForSdk(el)
      if (w >= 17 && h >= 17) player.resize(w, h)
    } catch (e) {}
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      run()
      window.setTimeout(run, 300)
      window.setTimeout(run, 800)
    })
  })
}

/**
 * 萤石 EZUIKitPlayer 容器：由父级 CSS 约束宽高，内部铺满或按测量像素创建（与多格直播一致）。
 *
 * @param {object} props
 * @param {string} props.accessToken
 * @param {string} [props.url] 完整 ezopen://...（回放须传）
 * @param {string} [props.deviceSerial] 与 channelNo 一起拼直播地址
 * @param {number} [props.channelNo=1]
 * @param {'live'|'playback'} [props.mode='live']
 * @param {boolean} [props.active=true] 为 false 时销毁实例（挂载节点仍保留，避免与 React 卸载冲突）
 * @param {boolean} [props.clickToPlay=false] 为 true 时须用户点击遮罩后才创建播放器；active 变 false 时重置为待点击
 * @param {import('react').ReactNode} [props.clickHint] 待播放时遮罩文案（默认可不传则用默认提示）
 * @param {import('react').CSSProperties} [props.clickOverlayStyle] 遮罩层样式合并到默认 overlay 样式
 * @param {(opened: boolean) => void} [props.onClickToPlayChange] 用户完成「点击开播」或 active 重置导致待播状态恢复时回调（opened=true 表示已开始取流）
 * @param {number} [props.resetStreamSignal] 与 clickToPlay 配合：值变化时强制关流并回到待点击（用于父级「停止」而不把 active 置 false）
 * @param {object} [props.playerProps] 并入 SDK 构造函数；handleSuccess / handleError 会与内置逻辑链式调用
 * @param {string[]} [props.plugins] 如 ['ptz'] → plugin
 * @param {number} [props.rotateDeg=0]
 * @param {string} [props.containerId] 挂载 DOM id，须全局唯一；不传则内部生成
 * @param {unknown} [props.recreateToken] 变化时若仍 active 则重建播放器（如宫格列数变化）
 * @param {number} [props.initDelayMs=0] 延迟创建播放器（毫秒），多路同时开播时可错开加载
 * @param {() => void} [props.onDestroy] 实例销毁并清空挂载点后回调（用于父级注销 player 引用）
 * @param {string} [props.wrapperClassName] 外层包裹（挂载点 + 点击遮罩）的 className
 * @param {import('react').CSSProperties} [props.wrapperStyle] 外层包裹样式（多格场景常设 position:absolute + inset:0）
 */
export default function EzvizPlayer ({
  accessToken,
  url: urlProp,
  deviceSerial,
  channelNo = 1,
  mode = 'live',
  active = true,
  clickToPlay = false,
  clickHint,
  clickOverlayStyle,
  onClickToPlayChange,
  resetStreamSignal,
  playerProps = {},
  plugins,
  rotateDeg = 0,
  className,
  style,
  containerClassName,
  containerStyle,
  containerId,
  recreateToken,
  initDelayMs = 0,
  onReady,
  onError,
  onDestroy,
  wrapperClassName,
  wrapperStyle
}) {
  const [streamStarted, setStreamStarted] = useState(false)
  const mountRef = useRef(null)
  const playerRef = useRef(null)
  const generatedIdRef = useRef(null)
  if (!generatedIdRef.current) {
    generatedIdRef.current = `ezviz-player-${Math.random().toString(36).slice(2)}-${Date.now()}`
  }
  const domId = containerId || generatedIdRef.current

  const playerPropsRef = useRef(playerProps)
  playerPropsRef.current = playerProps
  const pluginsRef = useRef(plugins)
  pluginsRef.current = plugins
  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError
  const onDestroyRef = useRef(onDestroy)
  onDestroyRef.current = onDestroy
  const onClickToPlayChangeRef = useRef(onClickToPlayChange)
  onClickToPlayChangeRef.current = onClickToPlayChange

  const initDelayMsRef = useRef(initDelayMs)
  initDelayMsRef.current = initDelayMs

  /** 与当前 SDK 实例一致的流身份；仅在身份变化或关流时销毁，避免父组件重渲染触发 cleanup destroy 导致闪黑、重复拉流 */
  const lastStreamIdentityRef = useRef(null)

  const effectiveActive = Boolean(active) && (!clickToPlay || streamStarted)

  useEffect(() => {
    if (!active) {
      setStreamStarted((prev) => {
        if (prev && clickToPlay) {
          try {
            onClickToPlayChangeRef.current?.(false)
          } catch (e) {}
        }
        return false
      })
    }
  }, [active, clickToPlay])

  const appliedResetRef = useRef(null)
  useEffect(() => {
    if (!clickToPlay) {
      if (resetStreamSignal !== undefined && resetStreamSignal !== null) {
        appliedResetRef.current = resetStreamSignal
      }
      return
    }
    if (resetStreamSignal === undefined || resetStreamSignal === null) return
    if (appliedResetRef.current === null) {
      appliedResetRef.current = resetStreamSignal
      return
    }
    if (appliedResetRef.current === resetStreamSignal) return
    appliedResetRef.current = resetStreamSignal
    setStreamStarted((was) => {
      if (was) {
        try {
          onClickToPlayChangeRef.current?.(false)
        } catch (e) {}
      }
      return false
    })
  }, [resetStreamSignal, clickToPlay])

  useEffect(() => {
    let cancelled = false
    let delayTimer = null

    const destroy = () => {
      lastStreamIdentityRef.current = null
      const player = playerRef.current
      playerRef.current = null
      if (player) {
        try {
          player.stop?.()
        } catch (e) {}
        try {
          player.destroy?.()
        } catch (e) {}
      }
      const el = mountRef.current
      if (el) {
        try {
          el.innerHTML = ''
        } catch (e) {}
      }
      try {
        onDestroyRef.current?.()
      } catch (e) {}
    }

    if (!effectiveActive) {
      destroy()
      return () => {}
    }

    const channelNoNorm = Number(channelNo) || 1
    const streamIdentity = [
      domId,
      String(deviceSerial || ''),
      channelNoNorm,
      String(accessToken || ''),
      mode,
      urlProp || '',
      Number(rotateDeg) || 0,
      recreateToken == null ? '' : String(recreateToken)
    ].join('\u0001')

    const playUrl = resolvePlayUrl({
      url: urlProp,
      deviceSerial,
      channelNo: channelNoNorm,
      mode
    })
    if (!accessToken || !playUrl) {
      destroy()
      return () => {}
    }

    const EZUIKitClass = getEZUIKitPlayerClass()
    if (!EZUIKitClass) {
      onErrorRef.current?.(new Error('EZUIKitPlayer 不可用'))
      return () => {}
    }

    const mountEl = mountRef.current
    if (!mountEl) {
      return () => {}
    }

    if (playerRef.current && lastStreamIdentityRef.current === streamIdentity) {
      return () => {
        cancelled = true
        if (delayTimer != null) {
          window.clearTimeout(delayTimer)
        }
      }
    }

    destroy()

    const LAYOUT_RETRY_MAX = 72

    const runCreate = (layoutAttempt = 0) => {
      if (cancelled) return
      const el = mountRef.current
      if (!el) return

      const layoutW = el.offsetWidth || el.clientWidth || 0
      const layoutH = el.offsetHeight || el.clientHeight || 0
      const layoutTooSmall = layoutW < 8 || layoutH < 8
      if (layoutTooSmall && layoutAttempt < LAYOUT_RETRY_MAX) {
        requestAnimationFrame(() => runCreate(layoutAttempt + 1))
        return
      }

      const {
        handleSuccess: userHandleSuccess,
        handleError: userHandleError,
        ...rawRestPlayerProps
      } = playerPropsRef.current || {}

      // 百分比/auto 等会覆盖下方像素宽高，SDK 在小挂载点上易黑屏；仅保留有限数值宽高交给调用方覆盖测量值
      const restPlayerProps = { ...rawRestPlayerProps }
      if (typeof restPlayerProps.width !== 'number' || !Number.isFinite(restPlayerProps.width)) {
        delete restPlayerProps.width
      }
      if (typeof restPlayerProps.height !== 'number' || !Number.isFinite(restPlayerProps.height)) {
        delete restPlayerProps.height
      }

      const { w, h } = measureMountRectForSdk(el)

      const isPlayback = mode === 'playback'
      const sizeOpts = isPlayback
        ? { width: w, height: h, template: 'pcRec' }
        : { width: w, height: h }

      const pluginsNow = pluginsRef.current

      let player
      try {
               player = new EZUIKitClass({
          id: domId,
          accessToken,
          url: playUrl,
          ...defaultLiveOptions,
          ...sizeOpts,
          ...restPlayerProps,
          ...(pluginsNow != null ? { plugin: pluginsNow } : {}),
          handleSuccess: () => {
            try {
              if (player && typeof player.displayStreamInfo === 'function') {
                player.displayStreamInfo(false)
              }
              if (player && typeof player.openSound === 'function') {
                player.openSound(false)
              }
              if (rotateDeg) {
                const wrap = document.getElementById(domId)
                if (wrap) {
                  const videoEl = wrap.querySelector('video')
                  const transform = `rotate(${rotateDeg}deg)`
                  if (videoEl) {
                    videoEl.style.transform = transform
                    videoEl.style.objectFit = 'contain'
                    videoEl.style.width = '100%'
                    videoEl.style.height = '100%'
                  } else wrap.style.transform = transform
                }
              }
            } catch (e) {}
            const sizeLocked = typeof restPlayerProps.width === 'number'
              && typeof restPlayerProps.height === 'number'
            if (!sizeLocked) {
              scheduleResizeAfterReady(player, domId)
            }
            try {
              userHandleSuccess?.()
            } catch (e) {}
            onReadyRef.current?.(player)
          },
          handleError: (err) => {
            try {
              userHandleError?.(err)
            } catch (e) {}
            onErrorRef.current?.(err)
          }
        })
      } catch (e) {
        onErrorRef.current?.(e)
        return
      }

      playerRef.current = player
      lastStreamIdentityRef.current = streamIdentity
    }

    const delayMs = Math.max(0, Number(initDelayMsRef.current) || 0)
    const kickoffRunCreate = () => {
      if (cancelled) return
      /** 点击开播后再测宽高：与遮罩同一帧测到的挂载点可能尚未铺满格子 */
      if (clickToPlay) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => runCreate(0))
        })
      } else {
        runCreate(0)
      }
    }
    if (delayMs > 0) {
      delayTimer = window.setTimeout(() => kickoffRunCreate(), delayMs)
    } else {
      kickoffRunCreate()
    }

    return () => {
      cancelled = true
      if (delayTimer != null) {
        window.clearTimeout(delayTimer)
      }
    }
    // 注意：不把 initDelayMs 列入依赖。cleanup 内不再 destroy，避免依赖数组因引用抖动重跑时误杀播放器；仅卸载或流身份变化时在 effect 主体内 destroy。
  }, [
    effectiveActive,
    accessToken,
    urlProp,
    deviceSerial,
    channelNo,
    mode,
    domId,
    rotateDeg,
    recreateToken,
    clickToPlay
  ])

  useEffect(() => {
    return () => {
      lastStreamIdentityRef.current = null
      const player = playerRef.current
      playerRef.current = null
      if (player) {
        try {
          player.stop?.()
        } catch (e) {}
        try {
          player.destroy?.()
        } catch (e) {}
      }
      const el = mountRef.current
      if (el) {
        try {
          el.innerHTML = ''
        } catch (e) {}
      }
      try {
        onDestroyRef.current?.()
      } catch (e) {}
    }
  }, [])

  const mergedClass = [className, containerClassName].filter(Boolean).join(' ')
  const mergedStyle = { ...style, ...containerStyle }

  const showClickOverlay =
    Boolean(clickToPlay) && Boolean(active) && !streamStarted

  const handleOverlayActivate = (e) => {
    e.stopPropagation()
    if (!active) return
    setStreamStarted(true)
    try {
      onClickToPlayChangeRef.current?.(true)
    } catch (err) {}
  }

  const handleOverlayKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    e.stopPropagation()
    handleOverlayActivate(e)
  }

  const mergedOverlayStyle = {
    ...overlayBaseStyle,
    ...clickOverlayStyle
  }

  return (
    <div
      className={wrapperClassName}
      style={{ position: 'relative', width: '100%', height: '100%', ...wrapperStyle }}
    >
      <div
        ref={mountRef}
        id={domId}
        className={mergedClass || undefined}
        style={{ ...mergedStyle, zIndex: 0 }}
      />
      {showClickOverlay ? (
        <div
          role="button"
          tabIndex={0}
          className="ezviz-player-click-overlay"
          style={mergedOverlayStyle}
          onClick={handleOverlayActivate}
          onKeyDown={handleOverlayKeyDown}
        >
          {clickHint != null ? (
            clickHint
          ) : (
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
              点击开始播放
            </span>
          )}
        </div>
      ) : null}
    </div>
  )
}
